import React, { CSSProperties, JSX, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { ChatOverlayView } from 'components/ChatOverlayView'
import { SourceMarkSeamProvider } from 'components/chatOverlays/sourceMarkLinks'
import { EmoteWallView } from 'components/EmoteWallView'
import { useOverlayPreviewSeam } from 'components/overlayPreview/usePreviewSeam'
import { OverlayPreviewSeam } from 'components/overlayPreview/previewSeam'
import { ChatPreviewFeed, WallPreviewFeed, createChatPreviewFeed, createWallPreviewFeed } from 'components/overlayPreview/previewFeeds'
import { chatPreviewConfigFromSummary, wallPreviewConfigFromSummary } from 'components/overlayPreview/previewConfig'
import { useAccountOverlayLink } from 'hooks/useAccountOverlayLink'
import { useOverlaySocketCredentialState } from 'hooks/useOverlaySocketCredential'
import { clipGlideTransitionOf } from 'lib/scenePresence'
import { ChatOverlaySummary, EmoteWallSummary } from 'types/api'

export type PreviewSourceKind = 'chat' | 'emotes' | 'stream'

export const previewSourceOf = (
  kind: 'chat' | 'emotes',
  row: { eventId?: string | null },
  id: string
): { kind: PreviewSourceKind, id: string } =>
  typeof row.eventId === 'string' && row.eventId !== ''
    ? { kind: 'stream', id: row.eventId }
    : { kind, id }

export interface PreviewStreamKey {
  eventId: string
  k: string | null
}

export const MAX_LIVE_PREVIEW_SOURCES = 8
export const SOURCE_RELEASE_GRACE_MS = 20 * 60 * 1000
export const SOURCE_UNRESOLVED_GRACE_MS = 15000

type Timer = ReturnType<typeof globalThis.setTimeout>

const noop = (): void => {}

const sourceKeyOf = (kind: PreviewSourceKind, id: string): string => `${kind}#${id}`

export interface ActiveSource {
  readonly key: string
  readonly kind: PreviewSourceKind
  readonly id: string
  readonly gen: number
}

interface SourceRecord extends ActiveSource {
  holders: number
  graceTimer: Timer | undefined
  ticket: string | null
  releasedSeq: number
  live: boolean
}

export interface SourceRegistry {
  hold: (kind: PreviewSourceKind, id: string) => () => void
  subscribe: (cb: () => void) => () => void
  active: () => readonly ActiveSource[]
  ticketOf: (key: string) => string | null
  setTicket: (key: string, gen: number, ticket: string | null) => void
  dispose: () => void
}

const NO_SOURCES: readonly ActiveSource[] = Object.freeze([])

export const createSourceRegistry = (): SourceRegistry => {
  const records = new Map<string, SourceRecord>()
  const listeners = new Set<() => void>()
  let active: readonly ActiveSource[] = NO_SOURCES
  let activeSig = ''
  let disposed = false
  let recomputeQueued = false
  let gens = 0
  let releases = 0

  const notify = (): void => {
    Array.from(listeners).forEach((cb) => cb())
  }

  const free = (rec: SourceRecord): void => {
    if (rec.graceTimer != null) globalThis.clearTimeout(rec.graceTimer)
    rec.graceTimer = undefined
    records.delete(rec.key)
  }

  const recompute = (): void => {
    recomputeQueued = false
    if (disposed) return
    const all = Array.from(records.values())
    const held = all.filter((r) => r.holders > 0)
    const waiting = all.filter((r) => r.holders === 0 && r.live).sort((a, b) => b.releasedSeq - a.releasedSeq)
    const live = [...held, ...waiting].slice(0, MAX_LIVE_PREVIEW_SOURCES)
    all.filter((r) => r.holders === 0 && !live.includes(r)).forEach(free)
    all.forEach((r) => { r.live = live.includes(r) })
    const sig = live.map((r) => `${r.key}:${r.gen}`).join(' ')
    if (sig === activeSig) return
    activeSig = sig
    active = live.length === 0
      ? NO_SOURCES
      : Object.freeze(live.map((r): ActiveSource => ({ key: r.key, kind: r.kind, id: r.id, gen: r.gen })))
    notify()
  }

  const membershipChanged = (): void => {
    if (disposed || recomputeQueued) return
    recomputeQueued = true
    globalThis.queueMicrotask(recompute)
  }

  const hold = (kind: PreviewSourceKind, id: string): (() => void) => {
    if (disposed || id === '') return noop
    const key = sourceKeyOf(kind, id)
    let rec = records.get(key)
    if (rec == null) {
      gens += 1
      rec = { key, kind, id, gen: gens, holders: 0, graceTimer: undefined, ticket: null, releasedSeq: 0, live: false }
      records.set(key, rec)
    }
    const held = rec
    held.holders += 1
    if (held.graceTimer != null) {
      globalThis.clearTimeout(held.graceTimer)
      held.graceTimer = undefined
    }
    if (held.holders === 1) membershipChanged()
    let released = false
    return () => {
      if (released) return
      released = true
      if (disposed || records.get(key) !== held) return
      held.holders = Math.max(0, held.holders - 1)
      if (held.holders > 0 || held.graceTimer != null) return
      releases += 1
      held.releasedSeq = releases
      held.graceTimer = globalThis.setTimeout(() => {
        held.graceTimer = undefined
        if (disposed || records.get(key) !== held || held.holders > 0) return
        records.delete(key)
        membershipChanged()
      }, held.ticket == null ? SOURCE_UNRESOLVED_GRACE_MS : SOURCE_RELEASE_GRACE_MS)
      membershipChanged()
    }
  }

  const subscribe = (cb: () => void): (() => void) => {
    if (disposed) return noop
    listeners.add(cb)
    return () => { listeners.delete(cb) }
  }

  const setTicket = (key: string, gen: number, ticket: string | null): void => {
    const rec = records.get(key)
    if (disposed || rec == null || rec.gen !== gen || rec.ticket === ticket) return
    rec.ticket = ticket
    notify()
  }

  const dispose = (): void => {
    disposed = true
    records.forEach((rec) => {
      if (rec.graceTimer != null) globalThis.clearTimeout(rec.graceTimer)
      rec.graceTimer = undefined
    })
    records.clear()
    listeners.clear()
    active = NO_SOURCES
    activeSig = ''
  }

  return {
    hold,
    subscribe,
    active: () => active,
    ticketOf: (key) => records.get(key)?.ticket ?? null,
    setTicket,
    dispose
  }
}

interface HubParts {
  chatFeed: ChatPreviewFeed
  wallFeed: WallPreviewFeed
  registry: SourceRegistry
}

interface HubValue {
  seam: OverlayPreviewSeam | null
  registry: SourceRegistry | null
}

const HubContext = createContext<HubValue | null>(null)

const PreviewTicketHost = ({ k, sourceKey, gen, registry }: { k: string, sourceKey: string, gen: number, registry: SourceRegistry }): null => {
  const { token } = useOverlaySocketCredentialState({ k })
  useEffect(() => {
    registry.setTicket(sourceKey, gen, token)
  }, [registry, sourceKey, gen, token])
  useEffect(() => () => registry.setTicket(sourceKey, gen, null), [registry, sourceKey, gen])
  return null
}

const PreviewCredentialHost = ({ kind, id, sourceKey, gen, registry }: { kind: 'chat' | 'emotes', id: string, sourceKey: string, gen: number, registry: SourceRegistry }): JSX.Element | null => {
  const link = useAccountOverlayLink(kind, id)
  const k = link.link == null ? null : link.link.k
  if (typeof k !== 'string' || k === '') return null
  return <PreviewTicketHost key={k} k={k} sourceKey={sourceKey} gen={gen} registry={registry} />
}

const StreamCredentialHost = ({ id, sourceKey, gen, registry, streamKey }: { id: string, sourceKey: string, gen: number, registry: SourceRegistry, streamKey: PreviewStreamKey | null }): JSX.Element | null => {
  if (streamKey == null || streamKey.eventId !== id) return null
  const k = streamKey.k
  if (typeof k !== 'string' || k === '') return null
  return <PreviewTicketHost key={k} k={k} sourceKey={sourceKey} gen={gen} registry={registry} />
}

const PreviewCredentialHosts = ({ registry, streamKey }: { registry: SourceRegistry, streamKey: PreviewStreamKey | null }): JSX.Element => {
  const active = useSyncExternalStore(registry.subscribe, registry.active, registry.active)
  return (
    <>
      {active.map((s) => (
        s.kind === 'stream'
          ? <StreamCredentialHost key={`${s.key}:${s.gen}`} id={s.id} sourceKey={s.key} gen={s.gen} registry={registry} streamKey={streamKey} />
          : <PreviewCredentialHost key={`${s.key}:${s.gen}`} kind={s.kind} id={s.id} sourceKey={s.key} gen={s.gen} registry={registry} />
      ))}
    </>
  )
}

export const EditorPreviewHubProvider = ({ children, streamKey = null }: { children: ReactNode, streamKey?: PreviewStreamKey | null }): JSX.Element => {
  const [parts, setParts] = useState<HubParts | null>(null)
  useEffect(() => {
    const next: HubParts = {
      chatFeed: createChatPreviewFeed(),
      wallFeed: createWallPreviewFeed(),
      registry: createSourceRegistry()
    }
    setParts(next)
    return () => {
      next.registry.dispose()
      next.chatFeed.dispose()
      next.wallFeed.dispose()
    }
  }, [])
  const seam = useOverlayPreviewSeam({
    mode: 'live',
    chatFeed: parts == null ? null : parts.chatFeed,
    wallFeed: parts == null ? null : parts.wallFeed
  })
  const value = useMemo((): HubValue => ({
    seam: parts == null ? null : seam,
    registry: parts == null ? null : parts.registry
  }), [parts, seam])
  return (
    <HubContext.Provider value={value}>
      <SourceMarkSeamProvider seam={value.seam}>{children}</SourceMarkSeamProvider>
      {parts != null && <PreviewCredentialHosts registry={parts.registry} streamKey={streamKey} />}
    </HubContext.Provider>
  )
}

export const useEditorPreviewSource = (
  kind: PreviewSourceKind,
  id: string
): { seam: OverlayPreviewSeam | null, token: string | null } | null => {
  const hub = useContext(HubContext)
  const registry = hub == null ? null : hub.registry
  const seam = hub == null ? null : hub.seam
  const seamReady = seam != null
  useEffect(() => {
    if (registry == null || !seamReady || id === '') return
    return registry.hold(kind, id)
  }, [registry, seamReady, kind, id])
  const key = sourceKeyOf(kind, id)
  const subscribe = useCallback(
    (cb: () => void): (() => void) => (registry == null ? noop : registry.subscribe(cb)),
    [registry]
  )
  const getTicket = useCallback(
    (): string | null => (registry == null ? null : registry.ticketOf(key)),
    [registry, key]
  )
  const token = useSyncExternalStore(subscribe, getTicket, getTicket)
  if (hub == null) return null
  return { seam, token: seamReady ? token : null }
}

const clipBoxStyleOf = (width: number, height: number, clipGlideMs: number): CSSProperties => ({
  width,
  height,
  overflow: 'hidden',
  position: 'relative',
  ...(clipGlideMs > 0 ? { transition: clipGlideTransitionOf(clipGlideMs) } : null)
})

const EDITOR_FRAME_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  border: '2px dashed rgba(255,255,255,0.2)',
  borderRadius: 8,
  pointerEvents: 'none'
}

const NoHubPlaceholder = ({ name, label, layoutWidth, layoutHeight, clipGlideMs }: {
  name: string
  label: string
  layoutWidth: number
  layoutHeight: number
  clipGlideMs: number
}): JSX.Element => (
  <div
    style={{ width: layoutWidth, height: layoutHeight, ...(clipGlideMs > 0 ? { transition: clipGlideTransitionOf(clipGlideMs) } : null) }}
    className='flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-white/25 bg-white/5 px-6 text-center'
  >
    <span style={{ fontSize: 34 }} className='font-semibold text-white/80'>{name}</span>
    <span style={{ fontSize: 26 }} className='text-white/50'>{label}</span>
  </div>
)

export const ChatCanvasPreview = ({ chat, slug, layoutWidth, layoutHeight, clipGlideMs = 0 }: {
  chat: ChatOverlaySummary
  slug?: string
  layoutWidth: number
  layoutHeight: number
  clipGlideMs?: number
}): JSX.Element => {
  const held = previewSourceOf('chat', chat, chat.chatId)
  const source = useEditorPreviewSource(held.kind, held.id)
  const viewSlug = held.kind === 'stream' ? slug : undefined
  const previewConfig = useMemo(() => chatPreviewConfigFromSummary(chat), [chat])
  if (source == null) {
    return <NoHubPlaceholder name={chat.name} label='Chat overlay, renders on the live overlay' layoutWidth={layoutWidth} layoutHeight={layoutHeight} clipGlideMs={clipGlideMs} />
  }
  return (
    <div data-testid='editor-chat-preview' data-chat-id={chat.chatId} style={clipBoxStyleOf(layoutWidth, layoutHeight, clipGlideMs)}>
      {source.seam != null && (
        <ChatOverlayView
          slug={viewSlug}
          chatId={chat.chatId}
          token={source.token ?? ''}
          fillHost
          preview={source.seam}
          previewConfig={previewConfig}
        />
      )}
      <div style={EDITOR_FRAME_STYLE} />
    </div>
  )
}

export const EmoteWallCanvasPreview = ({ wall, slug, layoutWidth, layoutHeight, clipGlideMs = 0 }: {
  wall: EmoteWallSummary
  slug?: string
  layoutWidth: number
  layoutHeight: number
  clipGlideMs?: number
}): JSX.Element => {
  const held = previewSourceOf('emotes', wall, wall.emoteWallId)
  const source = useEditorPreviewSource(held.kind, held.id)
  const viewSlug = held.kind === 'stream' ? slug : undefined
  const previewConfig = useMemo(() => wallPreviewConfigFromSummary(wall), [wall])
  if (source == null) {
    return <NoHubPlaceholder name={wall.name} label='Emote wall, renders on the live overlay' layoutWidth={layoutWidth} layoutHeight={layoutHeight} clipGlideMs={clipGlideMs} />
  }
  return (
    <div data-testid='editor-wall-preview' data-wall-id={wall.emoteWallId} style={clipBoxStyleOf(layoutWidth, layoutHeight, clipGlideMs)}>
      {source.seam != null && (
        <EmoteWallView
          slug={viewSlug}
          emoteWallId={wall.emoteWallId}
          token={source.token ?? ''}
          fillHost
          preview={source.seam}
          previewConfig={previewConfig}
        />
      )}
      <div style={EDITOR_FRAME_STYLE} />
    </div>
  )
}
